import { useEffect, useRef, useState, useMemo } from 'react';
import mapContext from '../contexts/map-context';
import { getActiveFilters } from '../utils/activeFilters';
import { getRadiusInKm } from '../utils/distance';
import { useGeolocation } from './useMapGeolocation';
import { connectDealerToDealerContext } from '../utils/connectDealer';
import { ActionTypes } from '../contexts/reducers/map-reducer';
import useLazySearchByGeoLocation from './useLazySearchByGeoLocation';

export function useConnectDealerByGeo() {
  const dispatch = mapContext.useMapDispatch();

  const {
    userSelectedFilters,
    preselectedFilters,
    searchRadius,
    locale,
    intlSearchEnabled,
    distanceUnit,
    dealerContextService,
    connectedDealer,
  } = mapContext.useMapState();

  const activeFilters = useMemo(
    () => getActiveFilters(preselectedFilters, userSelectedFilters),
    [preselectedFilters, userSelectedFilters],
  );

  const [userCoordinates, setCoordinates] = useState<{ lat: number; lng: number }>({
    lat: 0,
    lng: 0,
  });
  const firstRun = useRef(true);

  const searchRadiusInKm = getRadiusInKm(searchRadius, distanceUnit);
  const { getUserPosition } = useGeolocation();

  const getUserLocation = async () => {
    const { coordinates } = await getUserPosition();
    if (coordinates) setCoordinates(coordinates as any);
  };

  const { loadDealers, loading, error, dealersResult } = useLazySearchByGeoLocation(
    searchRadiusInKm,
    userCoordinates.lat,
    userCoordinates.lng,
    intlSearchEnabled ? undefined : locale.countryCode,
    activeFilters,
    userCoordinates?.lat,
    userCoordinates?.lng,
  );

  useEffect(() => {
    getUserLocation();
  }, []);

  useEffect(() => {
    if (firstRun.current && userCoordinates.lat !== 0 && userCoordinates.lng !== 0) {
      firstRun.current = false; // flip first to avoid race
      loadDealers();
    }
  }, [userCoordinates.lat, userCoordinates.lng, loadDealers]);

  const lastConnectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (loading || error || !dealersResult?.length || !dealerContextService) return;

    const newDealerId = String(dealersResult[0].dealerId);

    if (connectedDealer === newDealerId || lastConnectedRef.current === newDealerId) return;

    connectDealerToDealerContext(newDealerId, dealerContextService, dispatch);
    dispatch({ type: ActionTypes.setConnectedDealer, payload: newDealerId });

    lastConnectedRef.current = newDealerId;
  }, [loading, error, dealersResult, dealerContextService, connectedDealer, dispatch]);
}
